import { Restore, Save } from "@mui/icons-material";
import {
	Box,
	IconButton,
	MenuItem,
	Select,
	TableCell,
	TableRow,
	Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import {
	Detail,
	Person,
	QueryStatus,
	SessionState,
	SettingsObject,
	postAction,
} from "../utils";

interface ExemptionsRowProps {
	datum: { [x: string]: any };
	application: string;
	status: string;
	submitted?: string;
	detailsQueryData: { [x: string]: Detail[] };
	settings: SettingsObject;
	people: Person[];
	sessionState: SessionState;
	setSessionState: (arg0: SessionState) => void;
	setPostStatus: (arg0: QueryStatus) => void;
	setDetailsModal: (arg0: string) => void;
	refetchExemptions: () => void;
}

export function ExemptionsRow({
	datum,
	application,
	status,
	submitted,
	detailsQueryData,
	settings,
	people,
	sessionState,
	setSessionState,
	setPostStatus,
	setDetailsModal,
	refetchExemptions,
}: ExemptionsRowProps) {
	const [newApplication, setNewApplication] = useState(application);
	const [newSubmitted, setNewSubmitted] = useState<string | undefined>(
		submitted
	);
	const [newStatus, setNewStatus] = useState(status);

	const isEdited =
		application !== newApplication ||
		status !== newStatus ||
		submitted !== newSubmitted;

	const resetExemptionFields = () => {
		setNewApplication(application);
		setNewSubmitted(submitted);
		setNewStatus(status);
	};

	const updateExemption = () => {
		setPostStatus(QueryStatus.LOADING);
		postAction(
			{
				action: "edit-exemption",
				caseId: datum["_id"],
				data: {
					application: newApplication,
					status: newStatus,
					submitted: newSubmitted,
				},
			},
			sessionState,
			setSessionState,
			() => {
				setPostStatus(QueryStatus.SUCCESS);
				refetchExemptions();
			},
			(e) => {
				console.error(e);
				setPostStatus(QueryStatus.FAILURE);
			}
		);
	};

	useEffect(() => {
		resetExemptionFields();
	}, [application, status, submitted]);

	return (
		<TableRow
			key={datum["_id"]}
			sx={{ ":hover": { background: "#212121" } }}
		>
			<TableCell>
				<IconButton
					disabled={!isEdited}
					onClick={() => updateExemption()}
				>
					<Save />
				</IconButton>
				<IconButton
					disabled={!isEdited}
					onClick={() => resetExemptionFields()}
				>
					<Restore />
				</IconButton>
			</TableCell>
			<TableCell sx={{ padding: "10px" }}>
				{dayjs(datum["caseOpenDate"]).format("MM/DD/YYYY hh:mm A")}
			</TableCell>
			<TableCell
				sx={{
					padding: "10px",
					cursor: "pointer",
					":hover": {
						textDecoration: "underline",
						textDecorationStyle: "dotted",
					},
				}}
				onClick={() => setDetailsModal(datum["_id"])}
			>
				{datum["Client Name$"] || datum["Client Name$_0"]}
			</TableCell>
			<TableCell sx={{ padding: "10px" }}>
				{`${(datum["attorneys"] || [])
					.map(
						(id: string) =>
							people.find(({ _id }) => _id === id)?.name
					)
					.join(", ")}${
					datum["secondaryAttorneys"]?.length > 0
						? ` (+${datum["secondaryAttorneys"].length})`
						: ""
				}`}
			</TableCell>
			<TableCell sx={{ padding: "10px" }}>
				{`${(datum["staffs"] || [])
					.map(
						(id: string) =>
							people.find(({ _id }) => _id === id)?.name
					)
					.join(", ")}${
					datum["secondaryStaffs"]?.length > 0
						? ` (+${datum["secondaryStaffs"].length})`
						: ""
				}`}
			</TableCell>
			<TableCell
				sx={{
					padding: "10px",
					minWidth: "160px",
				}}
			>
				<Select
					fullWidth
					value={newApplication}
					size="small"
					onChange={(event) => setNewApplication(event.target.value)}
				>
					{settings.exemptionApplicationTypes.map((type) => (
						<MenuItem key={type} value={type}>
							{type}
						</MenuItem>
					))}
				</Select>
			</TableCell>
			<TableCell
				sx={{
					padding: "5px",
					minWidth: "160px",
				}}
			>
				<DatePicker
					value={newSubmitted ? dayjs(newSubmitted) : null}
					slotProps={{
						textField: {
							size: "small",
							fullWidth: true,
						},
					}}
					onChange={(date) => setNewSubmitted(date?.toISOString())}
				/>
			</TableCell>
			<TableCell
				sx={{
					padding: "5px",
					minWidth: "160px",
				}}
			>
				<Select
					fullWidth
					value={newStatus}
					size="small"
					onChange={(event) => setNewStatus(event.target.value)}
				>
					{settings.exemptionStatusTypes.map((type) => (
						<MenuItem key={type} value={type}>
							{type}
						</MenuItem>
					))}
				</Select>
			</TableCell>
			<TableCell sx={{ padding: "5px" }}>
				{(detailsQueryData[datum["_id"]] || []).length === 0 ? (
					<Box
						sx={{
							display: "flex",
							justifyContent: "center",
						}}
					>
						<Typography
							sx={{
								color: (theme) => theme.palette.grey[600],
								fontSize: "12px",
							}}
						>
							No Notes
						</Typography>
					</Box>
				) : (
					<Box
						sx={{
							maxHeight: "180px",
							minWidth: "360px",
							overflow: "scroll",
							border: (theme) =>
								`1px solid ${theme.palette.grey[800]}`,
							borderRadius: "4px",
						}}
					>
						{(detailsQueryData[datum["_id"]] || []).map(
							({ _id, description, date }) => (
								<Box
									key={`${datum["_id"]}-${_id}`}
									sx={{
										borderBottom: (theme) =>
											`1px solid ${theme.palette.grey[800]}`,
										padding: "10px",
									}}
								>
									<Box
										sx={{
											color: (theme) =>
												theme.palette.grey[600],
											fontStyle: "italic",
											fontSize: "11px",
										}}
									>
										{dayjs(date).format(
											"MM/DD/YYYY hh:mm A"
										)}
									</Box>
									<Box>{description}</Box>
								</Box>
							)
						)}
					</Box>
				)}
			</TableCell>
		</TableRow>
	);
}
